import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/UI/Layouts";
import Content from "../../components/UI/Layouts/Content";
import { EditProgram, EditVideo, Seasons, Video } from "../../types";
import VideoModal from "../../components/ScreenProgramDetail/VideoModal/VideoModal";
import PageHeader from "../../components/common/PageHeader";
import { useTranslation, Trans } from "react-i18next";
import { useStores } from "../../hooks/useStores";
import { useParams } from "react-router-dom";
import {
  Tab,
  TabContent,
  TabList,
  TabPanel,
  TabWrap,
} from "../../components/UI/Tab";

import Text from "../../components/UI/Text";
import { Image } from "../../components/UI/Image";
import {
  StyledAdvText,
  StyledAProgramContent,
  StyledText,
  StyledTitleAD,
  TagsContainer,
} from "./style";
import { Container, Row } from "../../components/common/Grid";
import { Button } from "../../components/UI/Button";
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Breadcrumb, BreadcrumbItem } from "../../components/UI/Breadcrumb";
import ProgramModal from "../../components/common/Program/ProgramModal/ProgramModal";
import AddIcon from "@mui/icons-material/Add";
import { useSnackbar } from "notistack";
import { observer } from "mobx-react";
import Loading from "../../components/common/Loading";
import EmptySpace from "../../components/common/EmptySpace";
import moment from "moment";
import { Badge } from "../../components/UI/Badge";
import { VirtualizedTable } from "../../components/common/VirtualizedTable";
import { useHandleMediaFolderChange } from "../../hooks/vimeoFoldersAndVideos/useHandleMediaFolderChange";
import { toast } from "react-toastify";

interface RouteParams {
  id: string;
}

const ProgramDetail: React.FC = observer(() => {
  const { programStore, categoryStore } = useStores();
  const { id } = useParams<RouteParams>();
  const { t } = useTranslation();
  const [video, setVideo] = useState<Video | null>(null);
  const [defaultSeason, setDefaultSeason] = useState<Video["season"] | null>(
    null
  );
  const [defaultEpisodeNumber, setDefaultEpisodeNumber] = useState<
    Video["episodeNumber"] | null
  >(null);
  const [isModalEditProgramOpen, setIsModalEditProgramOpen] = useState(false);
  const [isModalVideoOpen, setIsModalVideoOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const modalEditProgramHandler = () => {
    setIsModalEditProgramOpen((open) => !open);
  };

  const modalVideoHandler = () => {
    setIsModalVideoOpen((open) => !open);
  };

  useEffect(() => {
    setIsLoading(true);
    categoryStore.getProgramCategories();
    categoryStore.getMultimediaCategories();
    programStore.getProgramByID(id).then(() => setIsLoading(false));
  }, [categoryStore, id, programStore]);

  const handleVideoSubmit = useCallback(
    async (data: EditVideo) => {
      setIsLoading(true);

      if (programStore.program && programStore.program.id) {
        data.programId = programStore.program.id;
      }
      data.highlighted === "isHighlighted"
        ? (data.highlighted = true)
        : (data.highlighted = false);
      if (data.categories) {
        data.categories = data.categories.map((x: any) => {
          return parseInt(x, 10);
        });
      }
      try {
        await programStore.addVideo(data).then(() => setIsLoading(false));
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        console.log("handleVideoSubmit error", error);
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
      programStore.getProgramByID(id);
      setVideo(null);
      modalVideoHandler();
    },
    [enqueueSnackbar, id, programStore, t]
  );

  const handleVideoUpdate = useCallback(
    async (data: EditVideo) => {
      setIsLoading(true);
      if (programStore.program && programStore.program.id) {
        data.programId = programStore.program.id;
      }
      data.highlighted === "isHighlighted"
        ? (data.highlighted = true)
        : (data.highlighted = false);

      if (data.categories) {
        data.categories = data.categories.map((x: any) => {
          return parseInt(x, 10);
        });
      }
      try {
        await programStore.updateVideo(data).then(() => setIsLoading(false));
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
      programStore.getProgramByID(id);
      setVideo(null);
      modalVideoHandler();
    },
    [enqueueSnackbar, id, programStore, t]
  );

  const handleVideosEpisodeNumbersUpdate = useCallback(
    async (video: Video, oldIndex: number, newIndex: number) => {
      setIsLoading(true);

      try {
        await programStore
          .updateVideosEpisodeNumbers(video, oldIndex, newIndex)
          .then(() => setIsLoading(false));
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
      programStore.getProgramByID(id);
    },
    [enqueueSnackbar, id, programStore, t]
  );

  const handleDeleteVideo = useCallback(
    async (videoID: string) => {
      setIsLoading(true);
      try {
        await programStore.deleteVideo(videoID).then(() => setIsLoading(false));
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }

      programStore.getProgramByID(id);
    },
    [enqueueSnackbar, id, programStore, t]
  );

  const handleProgramSubmit = useCallback(
    async (data: EditProgram) => {
      setIsLoading(true);
      data.categories = data.categories.map((x: any) => {
        return parseInt(x, 10);
      });
      try {
        await programStore.updateProgram(data).then(() => setIsLoading(false));
        enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
          variant: "success",
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
      modalEditProgramHandler();
    },
    [enqueueSnackbar, programStore, t]
  );

  const handleMediaFolderChange = useHandleMediaFolderChange({ programStore });

  const program = useMemo(() => {
    let program = programStore.program;
    let index: number = 1;
    let seasons: Seasons[] = [];

    if (program) {
      while (index < program.numberOfSeasons + 1) {
        let arrayOfVideo = program?.multimedia.filter(
          // eslint-disable-next-line no-loop-func
          (video: Video) => video.season === index
        );
        seasons.push({
          id: index,
          episodes: arrayOfVideo,
        });
        index++;
      }

      program.seasons = seasons;
    }
    return program;
  }, [programStore.program]);

  const columns = useMemo(() => {
    let cols: any = [
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.programDetail.column.title"),
        dataKey: "title",
        sorter: (a: { name: string }, b: { name: any }) =>
          a.name.localeCompare(b.name),
        render: (name: string) => name,
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.programDetail.column.url"),
        dataKey: "externalId",
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.programDetail.column.validFrom"),
        dataKey: "validFrom",
        render: (validFrom: Date) => moment(validFrom).format("DD/MM/YYYY"),
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.programDetail.column.validUntil"),
        dataKey: "validUntil",
        render: (validUntil: Date) => moment(validUntil).format("DD/MM/YYYY"),
      },
      {
        width: 90,
        flexGrow: 0,
        label: t("common.action"),
        dataKey: "id",
        render: (id: string, videoItem: Video) => {
          return (
            <>
              <IconButton
                aria-label="delete"
                onClick={() => {
                  setVideo(videoItem);
                  modalVideoHandler();
                }}
              >
                <EditIcon />
              </IconButton>
              <div></div>

              <PopupState variant="popover" popupId="demo-popup-popover">
                {(popupState) => (
                  <div>
                    <IconButton color="error" {...bindTrigger(popupState)}>
                      <DeleteIcon />
                    </IconButton>
                    <Popover
                      {...bindPopover(popupState)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                    >
                      <Typography sx={{ p: 1 }}>
                        {t("common.deletePopUp")}
                        <IconButton
                          color="error"
                          sx={{ p: 0 }}
                          onClick={() => handleDeleteVideo(id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Typography>
                    </Popover>
                  </div>
                )}
              </PopupState>
            </>
          );
        },
      },
    ];

    if (program?.typology === "TVShow") {
      cols.splice(2, 0, {
        width: 80,
        flexGrow: 1,
        label: t("screens.programDetail.column.videoNumber"),
        dataKey: "episodeNumber",
        render: (episodeNumber: string) => episodeNumber,
        sorter: (a: { episodeNumber: string }, b: { episodeNumber: string }) =>
          a.episodeNumber.localeCompare(b.episodeNumber),
      });
    }
    return cols;
  }, [t, program?.typology, handleDeleteVideo]);

  const handleAddSeasonSubmit = useCallback(async () => {
    if (program) {
      try {
        await programStore.updateProgram({
          ...program,
          categories:
            program.categories?.map((x: any) => {
              return parseInt(x, 10);
            }) || [],
          numberOfSeasons: program.numberOfSeasons + 1,
        });
        program.seasons
          ? program.seasons.push({
              id: program.numberOfSeasons + 1,
              episodes: [],
            })
          : (program.seasons = [
              {
                id: program.numberOfSeasons + 1,
                episodes: [],
              },
            ]);
      } catch (error: any) {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      }
    }
  }, [enqueueSnackbar, program, programStore]);

  const handleDeleteSeason = useCallback(
    async (seasonNumber: number) => {
      if (program) {
        setIsLoading(true);
        try {
          await programStore
            .deleteSeason(program.id, { seasonNumber: seasonNumber })
            .then(() => setIsLoading(false));
          enqueueSnackbar(t("common.actionExecutedSuccessfully"), {
            variant: "success",
          });
        } catch (error: any) {
          enqueueSnackbar(error.message, {
            variant: "error",
          });
        }

        await programStore.getProgramByID(id);
      }
    },
    [enqueueSnackbar, id, program, programStore, t]
  );

  useEffect(() => {
    if (programStore.vimeoMediaListError) {
      toast(t("messages.ui.vimeoMediaListError"), {
        position: "bottom-right",
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        type: "error",
        theme: "colored",
        onClose: () => (programStore.vimeoMediaListError = false),
      });
    }
  }, [programStore.vimeoMediaListError, t, programStore]);

  useEffect(() => {
    if (programStore.vimeoMediaFoldersError) {
      toast(t("messages.ui.vimeoMediaFoldersError"), {
        position: "bottom-right",
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        type: "error",
        theme: "colored",
        onClose: () => (programStore.vimeoMediaFoldersError = false),
      });
    }
  }, [programStore.vimeoMediaFoldersError, t, programStore]);

  return (
    <Layout hideFooter>
      <Content borderBottomWidth="1px" fullHeight align="top">
        <Breadcrumb my={"20px"}>
          <BreadcrumbItem path="/">
            {t("screens.program.breadcrumb.home")}
          </BreadcrumbItem>
          <BreadcrumbItem path="/programs">
            {t("screens.program.breadcrumb.programs")}
          </BreadcrumbItem>
          <BreadcrumbItem active>
            {programStore.program?.title || ""}
          </BreadcrumbItem>
        </Breadcrumb>

        {program?.tags && program?.tags.length > 0 && (
          <TagsContainer>
            {program?.tags.map((tag, index) => {
              return (
                <Badge key={index} pill>
                  {tag}
                </Badge>
              );
            })}
          </TagsContainer>
        )}
        <PageHeader
          title={program?.title || ""}
          buttonEnable={true}
          buttonText={t("screens.programDetail.pageHeader.buttonText")}
          handleButton={modalEditProgramHandler}
          icon={<EditIcon />}
        />

        {isLoading ? (
          <Loading />
        ) : (
          <StyledAProgramContent>
            {program && program.posterUrl && (
              <Image src={program.posterUrl} alt={program.title} fluid />
            )}

            {program && program.description && (
              <Text paddingTop="20px">{program.description}</Text>
            )}

            {program && program.ads && program.ads[0] && (
              <StyledAdvText>
                <StyledTitleAD>
                  {t("screens.programDetail.banner")}{" "}
                </StyledTitleAD>
                <StyledText>{program.ads[0].adCircuit.title} </StyledText>
              </StyledAdvText>
            )}

            {program && program.ads && program.ads[0] && (
              <StyledAdvText>
                <StyledTitleAD>
                  {t("screens.programDetail.adCircuit")}{" "}
                </StyledTitleAD>
                <StyledText>{program.ads[0].adCircuit.title} </StyledText>
              </StyledAdvText>
            )}
          </StyledAProgramContent>
        )}
        <TabWrap variation="line">
          {program?.typology === "TVShow" && (
            <TabList>
              {program &&
                program.seasons?.map((season) => (
                  <>
                    <Tab key={season.id}>
                      {t("screens.programDetail.season")} {season.id}
                    </Tab>
                    {season.id > 1 && (
                      <PopupState
                        variant="popover"
                        popupId={season.id + "delete_popup"}
                      >
                        {(popupState) => (
                          <div>
                            <IconButton
                              color="error"
                              {...bindTrigger(popupState)}
                            >
                              <DeleteIcon />
                            </IconButton>
                            <Popover
                              {...bindPopover(popupState)}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                              transformOrigin={{
                                vertical: "top",
                                horizontal: "left",
                              }}
                            >
                              <Typography sx={{ p: 1 }}>
                                <Trans i18nKey="screens.programDetail.deleteSeason">
                                  Attenzione, stai per eliminare la stagione{" "}
                                  {{ seasonNumber: season.id }} e tutti i suoi
                                  episodi. Sei sicuro di voler procedere?
                                </Trans>
                                <IconButton
                                  color="error"
                                  sx={{ p: 0 }}
                                  onClick={() => handleDeleteSeason(season.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Typography>
                            </Popover>
                          </div>
                        )}
                      </PopupState>
                    )}
                  </>
                ))}
              <Tab
                onClick={handleAddSeasonSubmit}
                disabled={
                  !program.seasons ||
                  program.seasons[program.seasons?.length - 1].episodes
                    .length === 0
                }
              >
                {t("common.add")}
                <AddIcon />
              </Tab>
            </TabList>
          )}
          <TabContent>
            {program &&
              program.seasons?.map((season) => {
                return (
                  <TabPanel key={season.id} style={{ paddingBottom: "10em" }}>
                    {(program?.typology === "TVShow" ||
                      (program?.typology === "Movie" &&
                        program.seasons &&
                        program.seasons[0].episodes.length < 1)) && (
                      <Container>
                        <Row
                          justifyContent={
                            program.typology === "TVShow" ? "end" : "center"
                          }
                        >
                          <Button
                            variant="contained"
                            // startIcon={<AddIcon />}
                            onClick={() => {
                              setDefaultEpisodeNumber(
                                season.episodes.length + 1
                              );
                              setDefaultSeason(season.id);
                              modalVideoHandler();
                            }}
                          >
                            <AddIcon />
                            {t(
                              program.typology === "TVShow"
                                ? "screens.programDetail.newEpisode"
                                : "screens.programDetail.newMovie"
                            )}
                          </Button>
                        </Row>
                      </Container>
                    )}

                    {isLoading ? (
                      <Loading />
                    ) : !isLoading && season.episodes.length > 0 ? (
                      <div
                        style={{
                          flex: "1 1 auto",
                          width: "100%",
                          height: `${(season.episodes.length + 1) * 50}px`,
                          display: "flex",
                          marginTop: "20px",
                        }}
                      >
                        <VirtualizedTable
                          key={season.id}
                          rowCount={season.episodes.length}
                          rowGetter={({ index }) => season.episodes[index]}
                          columns={columns}
                          rowStyle={(row) =>
                            row.index % 2 === 0 ? { background: "#EDF1FB" } : {}
                          }
                          data={season.episodes}
                          onRowSort={handleVideosEpisodeNumbersUpdate}
                        />
                      </div>
                    ) : (
                      <EmptySpace
                        title={t(
                          program.typology === "TVShow"
                            ? "screens.programDetail.emptyData"
                            : "screens.programDetail.emptyDataMovie"
                        )}
                        buttonEnable={false}
                      />
                    )}
                  </TabPanel>
                );
              })}
          </TabContent>
        </TabWrap>
      </Content>

      <VideoModal
        video={video}
        isSubmitting={false}
        isOpen={isModalVideoOpen}
        onClose={() => {
          setVideo(null);
          modalVideoHandler();
        }}
        onSubmit={video ? handleVideoUpdate : handleVideoSubmit}
        defaultSeason={defaultSeason}
        defaultEpisodeNumber={defaultEpisodeNumber}
        onChangeFolder={handleMediaFolderChange}
        medias={programStore.mediaList}
        program={program}
      />

      <ProgramModal
        program={programStore.program}
        isSubmitting={isLoading}
        isOpen={isModalEditProgramOpen}
        onClose={modalEditProgramHandler}
        onSubmit={handleProgramSubmit}
        onChangeFolder={handleMediaFolderChange}
        medias={programStore.mediaList}
      />
    </Layout>
  );
});

export default ProgramDetail;
